import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import React from 'react';

import { Mission } from '@flipster/apis/v1/userpools/types';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Button } from '@flipster/react/design-system/button';
import ArrowDownIcon from '@flipster/react/icon/generated/ArrowDownIcon';
import CandlestickIcon from '@flipster/react/icon/generated/CandlestickIcon';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import UserIcon from '@flipster/react/icon/generated/UserIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';
import webviewHandler from '@flipster/utils/webview-handler';

import { userPoolBunja } from '../user-pool.state';

interface MissionUI {
  title: string;
  icon: React.ReactNode;
  buttonText: string;
  onAction?: () => void;
}

const MissionsSection = () => {
  const t = useTranslations();
  const router = useRouter();
  const { userPoolDataAtom, eventStatusAtom } = useBunja(userPoolBunja);
  const userPool = useAtomValue(userPoolDataAtom);
  const eventStatus = useAtomValue(eventStatusAtom);

  if (!userPool) return null;
  const userTickets = userPool.missions.reduce((sum, mission) => sum + (mission.complete ? mission.ticketCount : 0), 0);

  const totalPossibleTickets = userPool.missions.reduce((sum, mission) => sum + mission.ticketCount, 0);
  const getMissionUI = (mission: Mission): MissionUI => {
    const missionType = mission.missionType;
    switch (missionType) {
      case 'KYC':
        return {
          title: 'Verify identity',
          icon: <UserIcon size={20} color={token('colors.content.accent.info')} />,
          buttonText: 'Verify',
          onAction: () => {
            webviewHandler.postMessage({ action: 'link', params: { page: 'user/kyc' } }, () =>
              router.push('/user/kyc'),
            );
          },
        };
      case 'DEPOSIT':
        return {
          title: `Deposit above\n${mission.requiredAmount} USDT`,
          icon: <ArrowDownIcon size={20} color={token('colors.content.accent.info')} />,
          buttonText: 'Deposit',
          onAction: () => {
            webviewHandler.postMessage({ action: 'link', params: { page: 'deposit' } }, () => router.push('/assets'));
          },
        };
      case 'TRADE':
        return {
          title: 'Make your first trade',
          icon: <CandlestickIcon size={20} color={token('colors.content.accent.info')} />,
          buttonText: 'Trade',
          onAction: () => {
            webviewHandler.postMessage({ action: 'link', params: { page: 'trade/latest' } }, () =>
              router.push('/trade'),
            );
          },
        };
      default:
        throw new Error(`Unknown mission type: ${missionType}`);
    }
  };

  return (
    <div
      className={stack({
        gap: 16,
        pt: 16,
        w: '100%',
      })}
    >
      <div
        className={stack({
          gap: 20,
          p: 20,
          rounded: 16,
          bg: 'background.default.level-1',
        })}
      >
        <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-3' })}>
          <FormattedMessage id='Autogen_common.r251126.s_Missions' />
        </p>
        {eventStatus === 'Upcoming' && (
          <div
            className={flex({
              px: 16,
              rounded: 8,
              bg: 'background.default.level-3',
            })}
          >
            <div
              className={stack({
                gap: 4,
                py: 12,
              })}
            >
              <div className={stack()}>
                <p
                  className={css({
                    textStyle: 'Text/M/Strong',
                    color: 'content.accent.promotion',
                  })}
                >
                  <FormattedMessage id='Autogen_common.r251126.s_Complete_your_missions_early' />
                </p>
                <p
                  className={css({
                    textStyle: 'Text/M/Regular',
                    color: 'content.default.level-5',
                  })}
                >
                  <FormattedMessage id='Autogen_common.r251126.s_Your_tickets_will_be_reflected_at_the_start_day_of_the_event' />
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Tickets Status */}
        <div className={stack({ gap: 4 })}>
          <div
            className={css({
              position: 'relative',
              h: 4,
              w: '100%',
              bg: 'background.default.level-3',
              rounded: 999,
              overflow: 'hidden',
            })}
          >
            {totalPossibleTickets > 0 && (
              <div
                className={css({
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  h: '100%',
                  bg: 'content.accent.info',
                  rounded: 999,
                  transition: 'width 0.3s ease',
                })}
                style={{ width: `${(userTickets / totalPossibleTickets) * 100}%` }}
              />
            )}
          </div>
          <div className={hstack({ gap: 2 })}>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.accent.info' })}>{userTickets}</p>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
              / {totalPossibleTickets}
              <FormattedMessage id='Autogen_common.r251126.s_total_tickets' />
            </p>
          </div>
        </div>

        <div className={stack()}>
          {userPool.missions.map((mission, index) => {
            const missionUI = getMissionUI(mission);

            return (
              <div
                key={`${mission.missionType}-${index}`}
                className={hstack({
                  gap: 16,
                  py: 12,
                })}
              >
                <div
                  className={center({
                    w: 32,
                    h: 32,
                    rounded: 9999,
                    bg: 'background.default.level-3',
                  })}
                >
                  {mission.complete ? (
                    <CheckCircleIcon size={32} color={token('colors.content.accent.info')} />
                  ) : (
                    missionUI.icon
                  )}
                </div>
                <div className={stack({ gap: 2, flex: 1 })}>
                  <p
                    className={css({
                      textStyle: 'Title/XS',
                      color: 'content.default.level-5',
                      whiteSpace: 'pre-wrap',
                    })}
                  >
                    {missionUI.title}
                  </p>
                  <p className={css({ textStyle: 'Text/M/Regular', color: 'content.accent.info' })}>
                    {mission.complete
                      ? t('Autogen_common.r251126.s_mission_ticketCount_Tickets_received', {
                          mission_ticketCount: mission.ticketCount,
                        })
                      : t('Autogen_common.r251126.s_Reward_mission_ticketCount_Tickets', {
                          mission_ticketCount: mission.ticketCount,
                        })}
                  </p>
                </div>
                {!mission.complete && eventStatus !== 'Closed' && (
                  <Button
                    hierarchy='primary'
                    size='small'
                    label={missionUI.buttonText}
                    onClick={() => {
                      segment.track(EarnEvent.EarnTabClickNewJoinerPoolMissionButton, {
                        completed_tickets: userTickets,
                        total_tickets: totalPossibleTickets,
                        button_text: missionUI.buttonText,
                        mission_title: missionUI.title,
                      });
                      missionUI.onAction?.();
                    }}
                    css={{ px: 16 }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MissionsSection;
